import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, IconButton } from "@mui/material";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";

const DRAG_START_PX = 6;
/** Ignore tiny movement on buttons so clicks still register after a horizontal swipe. */
const SUPPRESS_CLICK_ON_INTERACTIVE_PX = 12;
const SCROLL_EDGE_PX = 4;
/** Prevent chevron overlay toggling when width is near the scroll threshold. */
const SCROLL_EDGE_HYSTERESIS_PX = 12;
const INTERACTIVE_SELECTOR =
    "button, a, input, select, textarea, [role='button'], [role='combobox'], .MuiToggleButton-root, .MuiChip-root, .MuiSelect-root";

/** Controls that must receive normal clicks (e.g. MUI Select menu) — no toolbar drag/capture. */
const NO_DRAG_SELECTOR =
    ".MuiSelect-root, .MuiOutlinedInput-root, .MuiInputBase-root, [role='combobox'], [data-play-toolbar-no-drag]";

const SCROLL_ROW_SX = {
    display: "block",
    width: "100%",
    minWidth: 0,
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
    touchAction: "pan-x",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    cursor: "grab",
    "&::-webkit-scrollbar": { display: "none" },
    "&:active": { cursor: "grabbing" },
};

const TRACK_SX = {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    alignItems: "center",
    gap: 1,
    width: "max-content",
    flexShrink: 0,
    pl: 0.25,
    pr: 0.5,
};

const SCROLL_HINT_BTN_SX = {
    pointerEvents: "auto",
    opacity: 0.7,
    color: "text.secondary",
    bgcolor: "transparent",
    "&:hover": { opacity: 1, bgcolor: "action.hover" },
};

/** Horizontally scrollable play toolbar (swipe, drag, and a right chevron when actions overflow). */
export default function PlayToolbarScrollRow({ children, sx }) {
    const scrollRef = useRef(null);
    const dragRef = useRef(null);
    const suppressClickRef = useRef(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollHints = useCallback(() => {
        const el = scrollRef.current;
        if (!el) {
            setCanScrollRight((prev) => (prev ? false : prev));
            return;
        }
        const maxScroll = el.scrollWidth - el.clientWidth;
        setCanScrollRight((prev) => {
            const showThreshold = SCROLL_EDGE_PX + SCROLL_EDGE_HYSTERESIS_PX;
            const hideThreshold = SCROLL_EDGE_PX;
            const canScroll = maxScroll > (prev ? hideThreshold : showThreshold);
            const notAtEnd = el.scrollLeft < maxScroll - (prev ? hideThreshold : showThreshold);
            const next = canScroll && notAtEnd;
            return prev === next ? prev : next;
        });
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return undefined;
        updateScrollHints();
        const ro = new ResizeObserver(() => updateScrollHints());
        ro.observe(el);
        if (el.firstElementChild) {
            ro.observe(el.firstElementChild);
        }
        el.addEventListener("scroll", updateScrollHints, { passive: true });
        return () => {
            ro.disconnect();
            el.removeEventListener("scroll", updateScrollHints);
        };
    }, [updateScrollHints]);

    const scrollRight = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const step = Math.max(120, el.clientWidth * 0.65);
        el.scrollBy({ left: step, behavior: "smooth" });
    }, []);

    const endDrag = useCallback(() => {
        const el = scrollRef.current;
        const drag = dragRef.current;
        if (!drag) return;
        if (el && drag.pointerId != null) {
            try {
                el.releasePointerCapture(drag.pointerId);
            } catch {
                /* already released */
            }
        }
        dragRef.current = null;
        if (el) el.style.cursor = "";
        updateScrollHints();
    }, [updateScrollHints]);

    const onPointerDown = useCallback((e) => {
        const el = scrollRef.current;
        if (!el || e.button !== 0) return;
        if (e.target.closest("[data-play-toolbar-scroll-hint]")) return;
        if (e.target.closest(NO_DRAG_SELECTOR)) return;
        /** Let toolbar buttons, selects, and links receive normal clicks (no drag capture). */
        if (e.target.closest(INTERACTIVE_SELECTOR)) return;

        suppressClickRef.current = false;
        dragRef.current = {
            pointerId: e.pointerId,
            startX: e.clientX,
            startScrollLeft: el.scrollLeft,
            dragging: false,
            onInteractive: Boolean(e.target.closest(INTERACTIVE_SELECTOR)),
        };

        try {
            el.setPointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
    }, []);

    const onPointerMove = useCallback((e) => {
        const el = scrollRef.current;
        const drag = dragRef.current;
        if (!el || !drag || drag.pointerId !== e.pointerId) return;

        const dx = e.clientX - drag.startX;
        if (!drag.dragging) {
            if (Math.abs(dx) < DRAG_START_PX) return;
            drag.dragging = true;
            el.style.cursor = "grabbing";
        }

        if (e.cancelable) e.preventDefault();
        el.scrollLeft = drag.startScrollLeft - dx;
    }, []);

    const onPointerUp = useCallback(
        (e) => {
            const drag = dragRef.current;
            if (!drag || drag.pointerId !== e.pointerId) return;
            if (
                drag.dragging &&
                drag.onInteractive &&
                Math.abs(e.clientX - drag.startX) >= SUPPRESS_CLICK_ON_INTERACTIVE_PX
            ) {
                suppressClickRef.current = true;
            }
            endDrag();
        },
        [endDrag]
    );

    const onPointerCancel = useCallback(
        (e) => {
            const drag = dragRef.current;
            if (!drag || drag.pointerId !== e.pointerId) return;
            endDrag();
        },
        [endDrag]
    );

    const onClickCapture = useCallback((e) => {
        if (!suppressClickRef.current) return;
        e.preventDefault();
        e.stopPropagation();
        suppressClickRef.current = false;
    }, []);

    return (
        <Box sx={{ position: "relative", width: "100%", minWidth: 0, ...sx }}>
            <Box
                ref={scrollRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}
                onClickCapture={onClickCapture}
                sx={SCROLL_ROW_SX}
            >
                <Box sx={TRACK_SX}>{children}</Box>
            </Box>
            {canScrollRight ? (
                <Box
                    data-play-toolbar-scroll-hint
                    aria-hidden={false}
                    sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bottom: 0,
                        width: 44,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        pointerEvents: "none",
                        zIndex: 2,
                        background: (theme) =>
                            `linear-gradient(to left, ${theme.palette.background.default} 30%, ${theme.palette.background.default}99 55%, transparent 100%)`,
                    }}
                >
                    <IconButton
                        size="small"
                        onClick={scrollRight}
                        aria-label="Show more toolbar actions"
                        sx={SCROLL_HINT_BTN_SX}
                    >
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </Box>
            ) : null}
        </Box>
    );
}

PlayToolbarScrollRow.propTypes = {
    children: PropTypes.node,
    sx: PropTypes.object,
};
